import { SlotClient } from '@frontend/slot/api';
import { TransactionClient, TransactionUserClient } from '@frontend/transaction/api';
import { Transaction, TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { MobileWizardStateRecord } from '@frontend/wizard/mobile';

export interface PackageLocation {
    spot_id?: string;
    module_id?: string;
    slot_id?: string;
}

export interface SyncWizardTransactionResult {
    transaction: Transaction;
    senders: TransactionUser[];
    receivers: TransactionUser[];
    location: PackageLocation;
}

export async function syncTransactionUsers(
    accountId: string,
    transactionId: string,
    type: TransactionUserType,
    desiredUserIds: string[]
): Promise<TransactionUser[]> {
    const uniqueUserIds = [...new Set(desiredUserIds.filter(Boolean))];
    const remote = await TransactionUserClient.fetchTransactionUsers({ transaction_id: transactionId, type });
    const remoteUserIds = remote.results.map((user) => user.user_id);

    const usersToAdd = uniqueUserIds.filter((userId) => !remoteUserIds.includes(userId));
    const usersToRemove = remote.results.filter((user) => !uniqueUserIds.includes(user.user_id));

    const addedUsers: TransactionUser[] = [];
    for (const userId of usersToAdd) {
        addedUsers.push(
            await TransactionUserClient.postTransactionUser(accountId, transactionId, {
                user_id: userId,
                type
            })
        );
    }

    for (const user of usersToRemove) {
        await TransactionUserClient.deleteTransactionUser(accountId, transactionId, user.user_id);
    }

    return [...addedUsers, ...remote.results.filter((user) => uniqueUserIds.includes(user.user_id))];
}

export async function resolvePackageLocation(store: MobileWizardStateRecord, transaction: Transaction): Promise<PackageLocation> {
    const spotId = (store.data.spotId as string | null | undefined) ?? transaction.spot_id ?? undefined;
    const slotId = (store.data.slotId as string | null | undefined) ?? transaction.slot_id ?? undefined;
    const storedModuleId = store.data.moduleId as string | null | undefined;

    if (slotId) {
        const slot = (await SlotClient.resolveSlots([slotId])).results[0];
        if (slot) {
            return {
                spot_id: spotId ?? slot.spot_id,
                module_id: slot.module_id,
                slot_id: slot.id
            };
        }
    }

    return {
        spot_id: spotId ?? undefined,
        module_id: storedModuleId ?? transaction.module_id ?? undefined,
        slot_id: slotId ?? undefined
    };
}

/**
 * Persists wizard store selections (senders, receivers, spot, slot) to the transaction API.
 * Safe to call before package creation or transaction processing — also handles tab navigation
 * where individual screen submits were skipped.
 */
export async function syncWizardTransactionData(store: MobileWizardStateRecord): Promise<SyncWizardTransactionResult> {
    const transaction = store.data.transaction as Transaction | undefined;
    if (!transaction?.id) {
        throw new Error('No transaction available to sync');
    }

    const accountId = store.wizard.account_id;
    const transactionId = transaction.id;

    const storeSenders = (store.data.senders as TransactionUser[] | undefined) || [];
    const storeReceivers = (store.data.receivers as TransactionUser[] | undefined) || [];
    const receiverFromStore = store.data.receiver as TransactionUser | undefined;
    const shouldSyncSenders = Array.isArray(store.data.senders);
    const shouldSyncReceivers = Array.isArray(store.data.receivers) || !!receiverFromStore;

    const senderUserIds = storeSenders.map((sender) => sender.user_id).filter((userId): userId is string => !!userId);
    const receiverUserIds = [...storeReceivers.map((receiver) => receiver.user_id), ...(receiverFromStore?.user_id ? [receiverFromStore.user_id] : [])].filter(
        (userId): userId is string => !!userId
    );

    const [senders, receivers] = await Promise.all([
        shouldSyncSenders
            ? syncTransactionUsers(accountId, transactionId, TransactionUserType.SENDER, senderUserIds)
            : TransactionUserClient.fetchTransactionUsers({ transaction_id: transactionId, type: TransactionUserType.SENDER }).then(
                  (response) => response.results
              ),
        shouldSyncReceivers
            ? syncTransactionUsers(accountId, transactionId, TransactionUserType.RECEIVER, receiverUserIds)
            : TransactionUserClient.fetchTransactionUsers({ transaction_id: transactionId, type: TransactionUserType.RECEIVER }).then(
                  (response) => response.results
              )
    ]);

    const location = await resolvePackageLocation(store, transaction);
    const spotChanged = (location.spot_id ?? null) !== (transaction.spot_id ?? null);
    const slotChanged = (location.slot_id ?? null) !== (transaction.slot_id ?? null);

    let updatedTransaction = transaction;
    if (spotChanged || slotChanged) {
        updatedTransaction = await TransactionClient.patchTransaction(accountId, transactionId, {
            spot_id: location.spot_id ?? null,
            slot_id: location.slot_id ?? null
        });
    }

    return {
        transaction: {
            ...updatedTransaction,
            spot_id: location.spot_id ?? updatedTransaction.spot_id,
            slot_id: location.slot_id ?? updatedTransaction.slot_id,
            module_id: location.module_id ?? updatedTransaction.module_id
        },
        senders,
        receivers,
        location
    };
}
